import { Fragment } from "react";
import { BarChart, Bar, AreaChart, Area, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, CartesianGrid, ReferenceLine } from "recharts";
import { Button, Table, ButtonGroup } from "react-bootstrap"

import { dateYYYYMMMDD, dateYYYYMMMDDHHMM, getISOWeek, getWeekDates, createBlobDownload } from "../../services/commonParse";
const dateAxisFormat = (date) => {
  return dateYYYYMMMDD(new Date(date))
};

export const ControlPanel = ({ stackState, timeZoom, countZoom, setMod, selectDisplay, catDisplay }) => {
  const setKey = (value) => { return (k) => { selectDisplay[k].state[1](value); } }
  return <Table striped bordered>
    <thead>
      <tr>
        <th rowSpan={2}>Plot controls</th>
        <th colSpan={2}>Geometry Selection
          <ButtonGroup size="sm" style={{ marginLeft: "1em" }}>
            <Button onClick={() => { Object.keys(selectDisplay).forEach(setKey(true)); }} >All</Button>
            <Button onClick={() => {
              Object.keys(selectDisplay).forEach(setKey(false));
              Object.keys(selectDisplay).filter((k) => k.endsWith("Full")).forEach(setKey(true));
            }}>All Fulls</Button>
            <Button onClick={() => {
              Object.keys(selectDisplay).forEach(setKey(false))
              Object.keys(selectDisplay).filter((k) => !k.endsWith("Full")).forEach(setKey(true));
            }}>All Partials</Button>
          </ButtonGroup>
        </th>
        <th rowSpan={2}>Category Selection</th>
      </tr>
      <tr>
        <th>
          CuW/CEE variants
          <ButtonGroup size={"sm"} style={{ marginLeft: "1em" }}>
            <Button onClick={() => {
              Object.keys(selectDisplay).forEach(setKey(false));
              Object.keys(selectDisplay).filter((k) => k.startsWith("CuW")).forEach(setKey(true));
            }} >All CuW</Button><br />
            <Button onClick={() => {
              Object.keys(selectDisplay).forEach(setKey(false));
              Object.keys(selectDisplay).filter((k) => k.startsWith("CuW") && k.endsWith("Full")).forEach(setKey(true));
            }}>CuW Fulls</Button><br />
            <Button onClick={() => {
              Object.keys(selectDisplay).forEach(setKey(false))
              Object.keys(selectDisplay).filter((k) => k.startsWith("CuW") && !k.endsWith("Full")).forEach(setKey(true));
            }}>CuW Partials</Button>
          </ButtonGroup>
        </th>
        <th>
          Ti/CEH variants
          <ButtonGroup size="sm" style={{ marginLeft: "1em" }}>
            <Button onClick={() => {
              Object.keys(selectDisplay).forEach(setKey(false));
              Object.keys(selectDisplay).filter((k) => k.startsWith("Ti")).forEach(setKey(true));
            }} >All Ti</Button><br />
            <Button onClick={() => {
              Object.keys(selectDisplay).forEach(setKey(false));
              Object.keys(selectDisplay).filter((k) => k.startsWith("Ti") && k.endsWith("Full")).forEach(setKey(true));
            }}>Ti Fulls</Button><br />
            <Button onClick={() => {
              Object.keys(selectDisplay).forEach(setKey(false))
              Object.keys(selectDisplay).filter((k) => k.startsWith("Ti") && !k.endsWith("Full")).forEach(setKey(true));
            }}>Ti Partials</Button>
          </ButtonGroup>
        </th>
      </tr>
    </thead>
    <tbody>
      <tr>
        <td><DisplaySelector stackState={stackState} timeZoom={timeZoom} countZoom={countZoom} setMod={setMod} /></td>
        <td><BPTypeSelector selectDisplay={selectDisplay} title={"CuW"} setMod={setMod} /></td>
        <td><BPTypeSelector selectDisplay={selectDisplay} title={"Ti"} setMod={setMod} /></td>
        <td><CatSelector catDisplay={catDisplay} setMod={setMod} /></td>
      </tr>
    </tbody>
  </Table >
}

const SimpleCheckBox = ({ setMod, label, state }) => {
  const [Var, setVar] = state;
  return <><input type="checkbox" onChange={(e) => { setVar(e.target.checked); setMod((m) => m + 1); }} checked={Var} />{label} < br /></>
}

const DisplaySelector = ({ stackState, timeZoom, countZoom, setMod }) => {
  return <>
    <SimpleCheckBox setMod={setMod} state={stackState} label="Display stacked" />
    <SimpleCheckBox setMod={setMod} state={timeZoom} label="Zoom project (x)" />
    <SimpleCheckBox setMod={setMod} state={countZoom} label="Zoom project (y)" />
  </>
}

const BPTypeSelector = ({ selectDisplay, title, setMod }) => {
  return Object
    .keys(selectDisplay)
    .filter((k) => k.startsWith(title))
    .map((k) => {
      return <SimpleCheckBox key={`select_${title}_${k}`} setMod={setMod} label={k} state={selectDisplay[k].state} />
    })
}

export const createDepsList = (selectDisplay) => {
  const deps = [];
  for (const key of Object.keys(selectDisplay)) {
    deps.push(selectDisplay[key].state[0])
  }
  return deps;
}

export const CatSelector = ({ catDisplay, setMod }) => {
  return <>
    {Object.keys(catDisplay).reverse().map((k) => {
      return <SimpleCheckBox key={`select_cat_${k}`} setMod={setMod} state={catDisplay[k].state} label={catDisplay[k].name} />
    })}
  </>
}

const dateToNum = (x) => x ? new Date(x) - new Date(0) : 0;



export const CumulativeNew = ({ showStacked, timeZoom, countZoom, target, keyState, dataTable, catDisplay, downloadPrefix }) => {
  const [keyVal,] = keyState
  const makeVlabel = (value) => ({ value, angle: -90, position: "insideTopRight" });

  // Dynamic elements to adjust when data table loads in
  const xdomain = [0, dateToNum(null)];
  const ydomain = [0, 10];
  let prodTotal = 0;
  if (dataTable.length) {
    const last = dataTable[dataTable.length - 1]
    xdomain[0] = dateToNum(dataTable[0].date);  // Minimum x value
    xdomain[1] = Math.max(xdomain[1], timeZoom ? dateToNum("2027-03-31") : last.date);
    for (const cat in last) {
      if (catDisplay[cat] && catDisplay[cat].state[0]) {
        prodTotal += last[cat];
      }
    }
    ydomain[1] = countZoom ? Math.round(target * 1.10) : Math.round(prodTotal * 1.35);
  }

  // Supporting dynamic elements
  const prodFraction = `( ${(100 * (prodTotal / target)).toFixed(1)}%)`

  const calcTotal = (l, v, i) => i === 0 ? l : l + v

  const ttLabel = (label, props) => {
    if (props.length) {
      const entry = props[0].payload;
      const total = Object.values(entry).reduce(calcTotal, 0);
      return `${dateYYYYMMMDDHHMM(new Date(label))} (Total: ${total})`;
    }
  }
  const ttContent = (v, name, props) => {
    const entry = props.payload;
    const den = Object.values(entry).reduce(calcTotal, 0)
    return `${v} (${(v / den * 100).toFixed(2)}%)`;
  }

  return <Fragment>
    <h4>Cummulative tally<Button style={{ marginLeft: "1em" }} onClick={() => {
      const blob = new Blob([JSON.stringify(dataTable.map((bp) => {
        const ret = { ...bp };
        ret.time = new Date(ret.time);// Date-list string would be generated
        return ret;
      }))
      ], { type: "text/plain" });
      createBlobDownload(blob, `${downloadPrefix}_cumulative_${dateYYYYMMMDD()}.json`)
    }}>Export data</Button></h4>
    <ResponsiveContainer width="100%" height={600} key={"tol_ind" + keyVal}>
      <AreaChart data={dataTable} margin={{ top: 5, right: 30, left: 20, bottom: 5, }}>
        <XAxis dataKey="date" type="number" tickFormatter={dateAxisFormat} tickCount={20} domain={xdomain} />
        <YAxis label={{ value: 'Cumulative total number of entries', angle: -90, position: 'insideLeft' }} domain={ydomain} />
        {[2024, 2025, 2026, 2027].map((x) => <Fragment key={`VLineGroup${x}`}>
          <ReferenceLine x={dateToNum(`${x}-01-01`)} label={makeVlabel(`${x}-Q1`)} key={`VLine${x}Q1`} />
          <ReferenceLine x={dateToNum(`${x}-04-01`)} label={makeVlabel(`${x}-Q2`)} key={`VLine${x}Q2`} />
          <ReferenceLine x={dateToNum(`${x}-07-01`)} label={makeVlabel(`${x}-Q3`)} key={`VLine${x}Q3`} />
          <ReferenceLine x={dateToNum(`${x}-10-01`)} label={makeVlabel(`${x}-Q4`)} key={`VLine${x}Q4`} />
        </Fragment>)}
        <ReferenceLine x={new Date("2027-03-31") - new Date(0)} label={{ ...makeVlabel("2027.Mar.31"), color: "red" }} stroke="red" key="dateEnd" />
        <ReferenceLine y={prodTotal} label={{ value: `Current: ${prodTotal}${prodFraction}`, position: "insideBottomLeft" }} stroke="blue" key="prodCurrent" />
        <ReferenceLine y={target} label={{ value: `Target: ${target}`, position: "insideBottomLeft" }} stroke="red" key="prodTarget" />
        <ReferenceLine y={target * 1.05} label={{ value: `5% spare`, position: "insideBottomLeft" }} stroke="red" strokeDasharray="3 3" key="prodTargetSpare" />
        <Tooltip labelFormatter={ttLabel} formatter={ttContent} />
        <Legend />
        {Object.keys(catDisplay).filter((key) => catDisplay[key].state[0]).map((key) => {
          const stack = showStacked ? "stack" : key
          const color = key in catDisplay ? catDisplay[key].color : "#BBB";
          const name = key in catDisplay ? catDisplay[key].name : "Unknown";
          return <Area
            key={`cumulative${keyVal}${key}${showStacked}`}
            dataKey={key}
            stackId={stack}
            stroke={color}
            fill={color}
            isAnimationActive={false}
            fillOpacity={showStacked ? 1 : 0}
            name={name} type="stepAfter"
          />
        })}
      </AreaChart>
    </ResponsiveContainer>
  </Fragment>
}

export const RateNew = ({ showStacked, timeZoom, keyVal, dataTable, catDisplay, downloadPrefix }) => {
  const dateToIdx = (date) => {
    const { year, week } = getISOWeek(date);
    return `${year}-W${week}`
  }

  const isLoaded = dataTable.length > 0;
  const weekIndex = [];

  // Making the list of indices used for binning
  let itDate = new Date(isLoaded ? dataTable[0].date : null);
  const endDate = new Date(timeZoom ? "2026-11-30" : isLoaded ? dataTable[dataTable.length - 1].date : null);
  while (itDate < new Date(endDate.getTime() + (7 * 24 * 3600 * 1000))) {
    weekIndex.push(dateToIdx(itDate));
    itDate = new Date(itDate.getTime() + (7 * 24 * 3600 * 1000));
  }

  let dataList = weekIndex.map((idx) => {
    const entry = {
      index: idx,
      unknown: 0,
    }
    const i = dataTable.findIndex((r) => dateToIdx(r.date) === idx);
    Object.keys(catDisplay).forEach((k) => entry[k] = i === -1 ? 0 : dataTable[i][k]);
    return entry;
  })

  const ttLabel = (label,) => {
    const [year, week] = label.split("-W");
    const { start, end } = getWeekDates(Number(year), Number(week));
    return `${label} (${dateYYYYMMMDD(start)}-${dateYYYYMMMDD(end)})`;
  }

  return <Fragment>
    <h4>
      Weekly tally
      <Button style={{ marginLeft: "1em" }} onClick={() => {
        const blob = new Blob([JSON.stringify(dataList)], { type: "text/plain" });
        createBlobDownload(blob, `${downloadPrefix}_perweek_${dateYYYYMMMDD()}.json`)
      }}>Export data</Button>
    </h4>
    <ResponsiveContainer width="100%" height={600} key={"total_rate" + keyVal}>
      <BarChart data={dataList} barGap={0}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis dataKey="index" />
        <YAxis label={{ value: 'Number of entries per week', angle: -90, position: 'insideLeft' }} />
        <Tooltip labelFormatter={ttLabel} />
        <Legend />
        {Object.keys(catDisplay).filter((key) => catDisplay[key].state[0]).map((key) => {
          const stack = showStacked ? "stack" : key;
          const color = catDisplay[key].color;
          const name = catDisplay[key].name;
          return <Bar
            dataKey={key}
            isAnimationActive={false}
            stackId={stack}
            fill={color}
            label={name}
            key={`"Rateplot"_${key}_${stack}`} />
        })}
      </BarChart>
    </ResponsiveContainer>
  </Fragment>
}


export const castToCumulativePlot = (dataTable) => {
  if (!dataTable.length) return dataTable;
  const dataSum = [...dataTable];
  for (let i = 0; i < dataTable.length; ++i) {
    dataSum[i].date = dateToNum(dataSum[i].date);
    if (i !== 0) {
      Object.keys(dataTable[i]).filter((k) => k !== "date").forEach((k) => {
        dataSum[i][k] = Number(dataSum[i - 1][k]) + Number(dataSum[i][k]);
      })
    }
  } // Adding one least entry to extend x axis
  const last = { ...dataSum[dataSum.length - 1] }
  last.date += 7 * 24 * 60 * 60 * 1000; // Add 7 days to the last entry
  dataSum.push(last);
  return dataSum
}

export const castToRatePlot = (dataTable) => {
  if (!dataTable.length) return dataTable;
  const dataSum = [...dataTable];
  for (let i = 0; i < dataTable.length; ++i) {
    dataSum[i].date = dateToNum(dataSum[i].date);
  }
  return dataSum
}

